import { call, put, select } from 'redux-saga/effects';
import { Action } from '@reduxjs/toolkit';
import i18n from 'i18n';
import { addActionNotification } from 'features/notifications/action-notifications/addActionNotification';
import { signalingQuestionLiveAnswerStarted } from 'features/qa/actions';
import { Question } from 'features/qa/types';
import { selectQuestionById } from 'features/qa/qaSlice';
import { selectSidePanel, sidePanelOpened } from 'features/side-panels/sidePanelsSlice';
import { SidePanelType } from 'features/side-panels/types';
import { FeatureFlag } from 'features/feature-set/types';
import { displaySimpleNotification } from 'features/notifications/simple-notifications/displaySimpleNotification';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';

export function* onLiveAnswerStartedSaga(
  action: ReturnType<typeof signalingQuestionLiveAnswerStarted>
) {
  const question: Question = yield select(selectQuestionById, action.payload.question.id);
  if (!question) {
    return;
  }

  const canSeeQuestionsAuthor: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.seeQuestionsAuthor
  );

  if (action.payload.local) {
    const showQuestionAuthor = canSeeQuestionsAuthor && question.user;
    const notificationContent = showQuestionAuthor
      ? i18n.t('notifications:qa.live_answer_started_local', {
          username: question.user!.name,
        })
      : i18n.t('notifications:qa.live_answer_started_anonymous_local');

    const notificationThunk: Action = yield call(displaySimpleNotification, {
      content: notificationContent,
    });
    yield put(notificationThunk);

    return;
  }

  const activePanel: SidePanelType = yield select(selectSidePanel);

  const actionNotificationThunk: Action = yield call(
    addActionNotification,
    {
      content: i18n.t('notifications:qa.live_answer_started.content', {
        username: action.payload.user.name,
        question: question.question,
      }),
      okText: i18n.t('notifications:qa.live_answer_started.btn_ok_label'),
      okAction: sidePanelOpened('qa', {
        questionId: question.id,
      }),
      hideOkAction: activePanel === 'qa',
      initiatorId: action.payload.user.id,
      scope: [FeatureFlag.qa, 'live-answer'],
      data: {
        questionId: question.id,
      },
    },
    {
      toastId: `${question.id}_${action.payload.id}`,
    }
  );

  yield put(actionNotificationThunk);
}
